'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { LayoutDashboard, FolderKanban, CheckSquare, Users, Settings, ChevronLeft, ChevronRight,  } from 'lucide-react';
import { LOGGED_IN_USER } from '@/lib/mockData';

interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: React.ReactNode;
  badge?: number;
}

const NAV_ITEMS: NavItem[] = [
  {
    id: 'nav-dashboard',
    label: 'Dashboard',
    href: '/',
    icon: <LayoutDashboard size={20} />,
  },
  {
    id: 'nav-projects',
    label: 'Projects',
    href: '/projects-page',
    icon: <FolderKanban size={20} />,
    badge: 3,
  },
  {
    id: 'nav-my-tasks',
    label: 'My Tasks',
    href: '/my-tasks',
    icon: <CheckSquare size={20} />,
    badge: 5,
  },
  {
    id: 'nav-team',
    label: 'Team',
    href: '/team',
    icon: <Users size={20} />,
  },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const pathname = usePathname();

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  return (
    <aside
      className="flex flex-col h-full sidebar-bg transition-all duration-300 ease-in-out"
      style={{ width: collapsed ? '64px' : '240px' }}
    >
      {/* Logo */}
      <div className="flex items-center h-16 px-3 border-b border-white/10 overflow-hidden">
        <div className="flex items-center gap-3 min-w-0">
          <AppLogo size={32} className="flex-shrink-0" />
          {!collapsed && (
            <span className="text-white font-semibold text-base whitespace-nowrap overflow-hidden">
              TaskFlow Pro
            </span>
          )}
        </div>
      </div>

      {/* Nav Items */}
      <nav className="flex-1 py-4 px-2 space-y-1 overflow-hidden">
        {/* Main section */}
        {!collapsed && (
          <p className="text-xs font-semibold uppercase tracking-widest text-white/30 px-3 pb-2">
            Main
          </p>
        )}
        {NAV_ITEMS.slice(0, 3).map((item) => (
          <Link
            key={item.id}
            href={item.href}
            title={collapsed ? item.label : undefined}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150 group relative ${
              isActive(item.href)
                ? 'bg-primary/20 text-white' :'text-white/60 hover:bg-white/10 hover:text-white/90'
            }`}
          >
            <span className={`flex-shrink-0 ${isActive(item.href) ? 'text-accent' : ''}`}>
              {item.icon}
            </span>
            {!collapsed && (
              <span className="text-sm font-medium whitespace-nowrap">{item.label}</span>
            )}
            {!collapsed && item.badge && (
              <span className="ml-auto bg-primary text-white text-xs font-semibold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                {item.badge}
              </span>
            )}
            {collapsed && item.badge && (
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-primary rounded-full" />
            )}
          </Link>
        ))}

        {/* People section */}
        {!collapsed && (
          <p className="text-xs font-semibold uppercase tracking-widest text-white/30 px-3 pt-4 pb-2">
            People
          </p>
        )}
        {!collapsed && <div className="h-px bg-white/10 mx-2 mb-2 mt-1" />}
        {NAV_ITEMS.slice(3).map((item) => (
          <Link
            key={item.id}
            href={item.href}
            title={collapsed ? item.label : undefined}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150 group ${
              isActive(item.href)
                ? 'bg-primary/20 text-white' :'text-white/60 hover:bg-white/10 hover:text-white/90'
            }`}
          >
            <span className={`flex-shrink-0 ${isActive(item.href) ? 'text-accent' : ''}`}>
              {item.icon}
            </span>
            {!collapsed && (
              <span className="text-sm font-medium whitespace-nowrap">{item.label}</span>
            )}
          </Link>
        ))}
      </nav>

      {/* Bottom section */}
      <div className="border-t border-white/10 p-3 space-y-1">
        <Link
          href="/settings"
          title={collapsed ? 'Settings' : undefined}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/60 hover:bg-white/10 hover:text-white/90 transition-all duration-150"
        >
          <Settings size={20} className="flex-shrink-0" />
          {!collapsed && <span className="text-sm font-medium">Settings</span>}
        </Link>

        {/* User profile */}
        <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/10 cursor-pointer transition-all duration-150 overflow-hidden">
          <div
            className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
            style={{ backgroundColor: LOGGED_IN_USER.avatarColor }}
          >
            {LOGGED_IN_USER.avatarInitials}
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <p className="text-sm font-medium text-white/90 truncate">{LOGGED_IN_USER.name}</p>
              <p className="text-xs text-white/40 truncate">{LOGGED_IN_USER.role}</p>
            </div>
          )}
        </div>

        {/* Collapse toggle */}
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-white/40 hover:text-white/80 hover:bg-white/10 transition-all duration-150"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          {!collapsed && <span className="text-xs">Collapse</span>}
        </button>
      </div>
    </aside>
  );
}